// view/audioControls.js — own controls for ONE <audio> in the backend (documents list, document group), replacing the
// native `controls` (plan bugfix_2026-10-08_polishing-patch, phase audio-steuerung). One row in the look of the video
// and PDF controls at size extrasmall: skip back · play/pause · skip forward · seek slider (takes the rest) · time
// "mm:ss/mm:ss" · sound on/off.
// A narrow row drops the duration (CSS container query, topo2/audio-controls.css). No volume slider and no speed menu
// (User 2026-10-08). Follows the element's own events, so the controls stay right whoever plays or seeks it.
// Play/pause, sound and the seek slider come from the shared base (mediaControls.js); this file adds the time text.

import { formatMmSs } from '../core/timeFormat.js';
import { createMediaControlsBase, createMediaSeekBar, ICON_BUTTON } from './mediaControls.js';
import { attachOverflowOnMount } from './overflowBar.js';

const SIZE = 'topo2-btn--extrasmall topo2-btn--inset';

const DEFAULT_LABELS = {   // fallbacks only — the caller passes translated labels (basic.js createAudioPlayer)
    playPause: 'Abspielen / Pausieren',
    skipBack:    'Zurück – gedrückt halten: schneller',
    skipForward: 'Vor – gedrückt halten: schneller',
    duration:  'Gesamtdauer',
    soundOff:  'Ton aus',
    soundOn:   'Ton an',
    seek:      'Springen',
    more:      'Weitere Bedienelemente',
};

// overflow: 'resizable' = too narrow → elements move into an extra bar behind "+" (view/overflowBar.js); 'fixed' = the
// row stays as it is. The row is its own container (width 100 %, container-type inline-size).
export function createAudioControls({ audio, labels = {}, overflow = 'resizable' } = {}) {
    const text = { ...DEFAULT_LABELS, ...labels };
    const { playBtn, skipBackBtn, skipForwardBtn, soundBtn, on, disposers } =
        createMediaControlsBase({ media: audio, prefix: 'audio', size: SIZE, text });

    const element = document.createElement('div');
    element.className = 'topo2-audio-controls topo2-btn-listnav topo2-listnav--solid topo2-xsmall';

    const seek = createMediaSeekBar({ media: audio, label: text.seek, inline: true });
    disposers.add(() => seek.destroy());

    const time = document.createElement('span');
    time.className = 'topo2-audio-time';
    const current = document.createElement('span');
    current.className = 'topo2-audio-time-current';
    const duration = document.createElement('span');
    duration.className = 'topo2-audio-time-duration';
    duration.title = text.duration;
    time.append(current, duration);

    element.append(skipBackBtn, playBtn, skipForwardBtn, seek.element, time, soundBtn);

    // Out first: the skip pair, the time, sound; play and the slider stay (User 2026-10-14).
    if (overflow === 'resizable') {
        const folding = attachOverflowOnMount({
            bar: element,
            within: '.topo2-audio-controls',
            priority: [[skipBackBtn, skipForwardBtn], [time], [soundBtn]],
            moreLabel: text.more,
            moreButtonClass: `${ICON_BUTTON} ${SIZE}`,
        });
        disposers.add(() => folding.destroy());
    }

    const showTime = () => { current.textContent = formatMmSs(audio.currentTime); };
    const showDuration = () => { duration.textContent = '/' + formatMmSs(audio.duration); };

    on(audio, 'timeupdate', showTime);
    on(audio, 'seeked', showTime);
    on(audio, 'loadedmetadata', showDuration);
    on(audio, 'durationchange', showDuration);

    showTime();
    showDuration();

    return { element, destroy() { disposers.dispose(); } };
}
